<script setup lang="ts">
import type { PropType } from "vue"
import type { ToastHtmlElement } from "@/types"
import { useMutation, useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { UseFocusTrap } from "@vueuse/integrations/useFocusTrap/component"
import { useForm } from "vee-validate"
import { inject, ref, watchEffect } from "vue"
import { z } from "zod"
import { editMedia } from "@/api"
import { BaseModal, Button, Card, Icon, Input, Textarea, Toast } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { Media } from "@/models"
import { EventVaultIdKey } from "@/providers/keys"
import { MediaEditModalKey } from "@/symbols"

const props = defineProps({
  media: {
    type: Object as PropType<Media>,
    required: true,
  },
})

const eventVaultId = inject(EventVaultIdKey, undefined)

const { t } = useTranslation()

const errorToast = ref<ToastHtmlElement | null>(null)
const successToast = ref<ToastHtmlElement | null>(null)

const validationSchema = toTypedSchema(
  z.object({
    title: z.string().min(1, t("media_edit_modal.error.title")),
    description: z.optional(z.string().max(1000, t("media_edit_modal.error.description"))),
  }),
)

const { toggle } = useModal(MediaEditModalKey)

const { setValues, errors, handleSubmit } = useForm({
  validationSchema,
})

const queryClient = useQueryClient()

const { mutate, isPending: isLoading } = useMutation({
  mutationKey: ["editMedia", props.media.id, eventVaultId],
  mutationFn: (media: Media) => editMedia(media, eventVaultId),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["listMedia"] })
    successToast.value?.showToast({ content: t("media_edit_modal.success"), title: t("success") })
    toggle()
  },
  onError: () => {
    errorToast.value?.showToast({ content: t("try_again"), title: t("something_went_wrong"), icon: "Caution" })
  },
})

const submit = handleSubmit((values) => {
  if (Object.keys(errors.value).length)
    return

  const updatedMedia = new Media({
    ...props.media,
    title: values.title,
    description: values.description,
  })

  mutate(updatedMedia)
})

watchEffect(() => {
  setValues({
    title: props.media.title,
    description: props.media.description,
  })
})
</script>

<template>
  <BaseModal :name="MediaEditModalKey">
    <UseFocusTrap>
      <Card class="gpx-edit-media">
        <div v-if="isLoading">
          <div class="gpx-edit-media-header">
            <div class="gpx-skeleton gpx-edit-media-heading_loading" />
            <Button variant="flat" @click="toggle">
              <span class="sr-only">{{ t("close") }}</span>
              <Icon icon="Close" />
            </Button>
          </div>
          <div class="gpx-edit-media-fields">
            <div>
              <div class="gpx-skeleton gpx-edit-modal-label_loading" />
              <div class="gpx-skeleton gpx-edit-modal-input_loading" />
            </div>
            <div>
              <div class="gpx-skeleton gpx-edit-modal-label_loading" />
              <div class="gpx-skeleton gpx-edit-modal-textarea_loading" />
            </div>
            <div class="gpx-edit-media-actions">
              <div class="gpx-skeleton gpx-edit-media-action_loading" />
              <div class="gpx-skeleton gpx-edit-media-action_loading" />
            </div>
          </div>
        </div>
        <div v-else>
          <div class="gpx-edit-media-header">
            <h2>{{ t("edit") }}</h2>
          </div>
          <form @submit.prevent="submit">
            <div class="gpx-edit-media-fields">
              <Input name="title" :label="t('title')" :placeholder="t('title')" />
              <Textarea name="description" :label="t('description')" :placeholder="t('description')" rows="4" />
              <div class="gpx-edit-media-actions">
                <Button size="lg">
                  {{ t("media_edit_modal.save") }}
                </Button>
                <Button size="lg" variant="flat" @click.prevent="toggle">
                  {{ t("cancel") }}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </Card>
    </UseFocusTrap>
  </BaseModal>

  <Toast ref="errorToast" variant="danger" />
  <Toast ref="successToast" variant="success" />
</template>

<style scoped>
.gpx-edit-media-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.gpx-edit-media-header .gpx-button {
  padding: 0;
}

.gpx-edit-media-header h2 {
  font-size: 1.25rem;
  white-space: nowrap;
  margin: 0;
}

.gpx-edit-media-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.gpx-lightbox-icon-button {
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  font-size: 0.75rem;
}

.gpx-edit-media-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.gpx-edit-media-heading_loading {
  width: 12rem;
  height: 2.5rem;
}

.gpx-edit-modal-label_loading {
  width: 10rem;
  height: 1.25rem;
  margin-bottom: 0.5rem;
}

.gpx-edit-modal-input_loading {
  height: 3rem;
}

.gpx-edit-modal-textarea_loading {
  height: 8rem;
}

.gpx-edit-media-action_loading {
  width: 8rem;
  height: 3rem;
}

@media (width >= 768px) {
  .gpx-edit-media-actions {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
}
</style>
